import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Modal, Text } from '@hopskipdrive/react-components';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { includes } from 'lodash';

import { Button } from '../../../shared/components/Button';
import { CopyToClipboard } from '../../../shared/components/CopyToClipboard';
import { SchoolBuildingIcon } from '../../../shared/components/Icons';
import { ButtonLink } from '../../../shared/components/Links';
import { colorPalette, colorTokens } from '../../../shared/components/variables';
import { useToast } from '../../../shared/contexts/ToastContext';
import { RideForm } from '../../components/RideForm';
import { RIDE_STATES } from '../../constants/rideStates';
import { useRebuildRideSubmit } from '../../hooks/useRebuildRideSubmit';
import { EditRideDetails } from '../../types/rides';
import { OutletContextType } from '../PageHeading';

import { rebuildRideValidationSchema } from './rebuildRideValidationSchema';

function useRebuildRide() {
  return useOutletContext<OutletContextType>();
}

const rideFormDefaults = {
  anchorTime: new Date(),
  timeAnchor: 'drop_off',
  time: '',
  waypointsAttributes: [
    {
      anchor: false,
      id: 0,
      passengerIdsStrings: [],
      accountLocationAttributes: [
        {
          address: '',
          accountId: 0,
          placeId: '',
          streetAddressTwo: '',
        },
      ],
    },
    {
      anchor: true,
      id: 0,
      passengerIdsStrings: [],
      accountLocationAttributes: [
        {
          address: '',
          accountId: 0,
          placeId: '',
          streetAddressTwo: '',
        },
      ],
    },
  ],
};

export const RebuildRideFormModal = () => {
  const { rideSlug, ridersOnRide, editRideDetails, accountDetails, timeZone, organizers, rideState } = useRebuildRide();
  const canRebuild = includes(RIDE_STATES.canceled, rideState);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { mutate, isPending } = useRebuildRideSubmit({
    rideSlug: rideSlug,
    successCallback: (response) => navigate(`/rides/${response?.rebuiltRideSlug}`),
    errorCallback: (
      error: Error & {
        errors: string[];
      }
    ) => {
      error?.errors
        ? error?.errors?.forEach((errorMessage) => {
            addToast({
              severity: 'error',
              message: errorMessage,
            });
          })
        : addToast({
            severity: 'error',
            message: error.message,
          });
    },
  });
  const methods = useForm<EditRideDetails>({
    defaultValues: useMemo(() => {
      return { ...rideFormDefaults, ...editRideDetails };
    }, [editRideDetails]),
    resolver: yupResolver(rebuildRideValidationSchema(timeZone)),
  });

  const handleSubmit = async (data) => {
    const rebuildRidePayload = {
      anchorTime: data.anchorTime,
      timeAnchor: data.timeAnchor,
      ...(data.waypointsAttributes && {
        waypointsAttributes: data.waypointsAttributes.map((waypoint) => {
          return {
            anchor: waypoint.anchor,
            passengerIdsStrings: !waypoint.anchor ? waypoint.passengerIdsStrings : [],
            accountLocationAttributes: waypoint.accountLocationAttributes,
          };
        }),
      }),
    };

    mutate(rebuildRidePayload);
  };

  return (
    <Modal open={canRebuild} maxWidth="lg" data-testid="rebuild-organizer-info">
      <Modal.Title>
        <Box display="flex" alignItems="center" gap="12px">
          <SchoolBuildingIcon style={{ display: 'block', height: '24px', width: '24px', alignSelf: 'center' }} />
          <Text variant="h6" textColor="primaryText">
            Account: {accountDetails.accountName}
          </Text>
          <CopyToClipboard
            toastMessage="Slug Copied"
            text={accountDetails.accountSlug}
            variant="h6"
            color={colorTokens.text.secondary}
          />
        </Box>
      </Modal.Title>
      <Modal.Content>
        <Alert
          severity="info"
          variant="standard"
          data-testid="rebuild-ride-info"
          title=""
          message={
            <>
              Please review payment and follow the line item process if necessary to ensure the CareDriver is paid the
              correct amount
            </>
          }
          style={{ marginTop: '12px' }}
          action={
            <Button
              color="tonal"
              variant="text"
              startIcon={<OpenInNewIcon />}
              style={{ border: `1px solid ${colorTokens.primary.outlineBorder}` }}
              onClick={() => window.open(window.env.CARE_REBUILD_LEARN_MORE_URL, '_blank')}
            >
              Learn More
            </Button>
          }
          onClose={() => {}}
        />
        <FormProvider {...methods}>
          <RideForm
            timeZone={timeZone}
            ridersOnRide={ridersOnRide}
            handleSubmit={handleSubmit}
            rideDetailsDefault={editRideDetails}
            isNewRide={true}
            organizers={organizers}
          />
        </FormProvider>
      </Modal.Content>
      <Modal.Actions style={{ borderTop: `1px solid ${colorPalette.neutralVariant90}` }}>
        <ButtonLink variant="text" to=".." disabled={isPending}>
          Never mind
        </ButtonLink>
        <Button type="submit" color="primary" form="ride-form" loading={isPending}>
          {isPending ? 'Rebuilding...' : 'Rebuild'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
